import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  TextField,
  Button,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  Chip,
  Divider,
  Container,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableFooter,
  Paper,
  Tabs,
  Tab,
  GlobalStyles,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Search as SearchIcon,
  Phone as PhoneIcon,
  Receipt as ReceiptIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  LocalShipping as LocalShippingIcon,
  Cancel as CancelIcon,
  Download as DownloadIcon,
  Person as PersonIcon,
  ShoppingCart as ShoppingCartIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  History as HistoryIcon,
  Print as PrintIcon
} from '@mui/icons-material';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';

const MyOrders = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [phone, setPhone] = useState('');
  const [orderCode, setOrderCode] = useState('');
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showFullDetails, setShowFullDetails] = useState(false);
  
  // Order history states
  const [orders, setOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [historyError, setHistoryError] = useState('');
  const [tabValue, setTabValue] = useState(0);
  
  // Get user from localStorage
  const [user, setUser] = useState(null);

  // CSS Animations
  const globalStyles = (
    <GlobalStyles
      styles={{
        '@keyframes fadeInUp': {
          from: {
            opacity: 0,
            transform: 'translateY(30px)',
          },
          to: {
            opacity: 1,
            transform: 'translateY(0)',
          },
        },
        '@keyframes zoomIn': {
          from: {
            opacity: 0,
            transform: 'scale(0.8)',
          },
          to: {
            opacity: 1,
            transform: 'scale(1)',
          },
        },
        '@keyframes slideInLeft': {
          from: {
            opacity: 0,
            transform: 'translateX(-50px)',
          },
          to: {
            opacity: 1,
            transform: 'translateX(0)',
          },
        },
        '@keyframes pulse': {
          '0%, 100%': {
            transform: 'scale(1)',
            opacity: 1,
          },
          '50%': {
            transform: 'scale(1.05)',
            opacity: 0.8,
          },
        },
        '@keyframes glow': {
          '0%, 100%': {
            boxShadow: '0 0 5px rgba(59, 93, 172, 0.5)',
          },
          '50%': {
            boxShadow: '0 0 20px rgba(59, 93, 172, 0.8), 0 0 30px rgba(59, 93, 172, 0.6)',
          },
        },
        '@keyframes statusPulse': {
          '0%, 100%': {
            transform: 'scale(1)',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          },
          '50%': {
            transform: 'scale(1.02)',
            boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
          },
        },
      }}
    />
  );
  
  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        setUser(parsedUser);
        setTabValue(0); // Default to order history for logged-in users
      } catch (err) {
        console.error('Error parsing user data:', err);
      }
    }
  }, []);

  // Fetch order history for logged-in user
  useEffect(() => {
    if (user?.id && tabValue === 0) {
      fetchOrderHistory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, tabValue]);

  const fetchOrderHistory = async () => {
    if (!user?.id) return;
    
    setHistoryLoading(true);
    setHistoryError('');
    
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/order_history.php`,
        { user_id: user.id },
        { withCredentials: true }
      );
      
      if (response.data.success) {
        setOrders(response.data.orders);
      } else if (response.data.error && response.data.error.toLowerCase().includes('تسجيل الدخول')) {
        localStorage.removeItem('user');
        setUser(null);
        setHistoryError('انتهت جلسة العمل، يرجى تسجيل الدخول مرة أخرى');
      } else {
        setHistoryError(response.data.error || 'حدث خطأ في تحميل البيانات');
      }
    } catch (err) {
      console.error('Order history fetch error:', err);
      setHistoryError('حدث خطأ أثناء جلب الطلبات');
    } finally {
      setHistoryLoading(false);
    }
  };

  const showOrderDetails = async (orderCode) => {
    setDetailsLoading(true);
    setSelectedOrder(orderCode);
    
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/order_status2.php`,
        { order_code: orderCode, user_id: user.id },
        { withCredentials: true }
      );
      
      if (response.data.success) {
        setResult(response.data); // Set result to use existing UI
        // Scroll to top to show the details
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } catch (err) {
      console.error('Order details fetch error:', err);
    } finally {
      setDetailsLoading(false);
    }
  };

  const closeOrderDetails = () => {
    setResult(null);
    setSelectedOrder(null);
  };

  // Order status configuration - Enhanced Arabic support
  const getOrderStatus = (status) => {
    const statusConfig = {
      // Primary status mappings (lowercase)
      'pending': {
        label: 'في الانتظار',
        color: 'warning',
        icon: <ScheduleIcon />
      },
      'confirmed': {
        label: 'مؤكد',
        color: 'info',
        icon: <CheckCircleIcon />
      },
      'processing': {
        label: 'قيد التجهيز',
        color: 'primary',
        icon: <ScheduleIcon />
      },
      'shipped': {
        label: ' قيد التوصيل',
        color: 'secondary',
        icon: <LocalShippingIcon />
      },
      'delivered': {
        label: 'تم التوصيل',
        color: 'success',
        icon: <CheckCircleIcon />
      },
      'cancelled': {
        label: 'ملغي',
        color: 'error',
        icon: <CancelIcon />
      },
      
      // Uppercase variants (for admin panel updates)
      'PENDING': {
        label: 'في الانتظار',
        color: 'warning',
        icon: <ScheduleIcon />
      },
      'CONFIRMED': {
        label: 'مؤكد',
        color: 'info',
        icon: <CheckCircleIcon />
      },
      'SHIPPED': {
        label: 'تم الشحن',
        color: 'secondary',
        icon: <LocalShippingIcon />
      },
      'DELIVERED': {
        label: 'تم التوصيل',
        color: 'success',
        icon: <CheckCircleIcon />
      },
      'CANCELLED': {
        label: 'ملغي',
        color: 'error',
        icon: <CancelIcon />
      },
      
      // Additional status variations
      'new': {
        label: 'جديد',
        color: 'info',
        icon: <ScheduleIcon />
      },
      'ready': {
        label: 'جاهز للشحن',
        color: 'primary',
        icon: <CheckCircleIcon />
      },
      'completed': {
        label: 'مكتمل',
        color: 'success',
        icon: <CheckCircleIcon />
      },
      'returned': {
        label: 'مُرتجع',
        color: 'error',
        icon: <CancelIcon />
      },
      'refunded': {
        label: 'مُسترد',
        color: 'error',
        icon: <CancelIcon />
      },
      
      // Handle mixed case and alternative spellings
      'Pending': {
        label: 'في الانتظار',
        color: 'warning',
        icon: <ScheduleIcon />
      },
      'Confirmed': {
        label: 'مؤكد',
        color: 'info',
        icon: <CheckCircleIcon />
      },
      'Shipped': {
        label: 'تم الشحن',
        color: 'secondary',
        icon: <LocalShippingIcon />
      },
      'Delivered': {
        label: 'تم التوصيل',
        color: 'success',
        icon: <CheckCircleIcon />
      },
      'Cancelled': {
        label: 'ملغي',
        color: 'error',
        icon: <CancelIcon />
      }
    };

    // Try exact match first, then lowercase, then return default
    return statusConfig[status] || 
           statusConfig[status?.toLowerCase()] || 
           statusConfig[status?.toUpperCase()] || {
             label: status || 'غير محدد',
             color: 'default',
             icon: <ScheduleIcon />
           };
  };

  // Helper function to get Arabic status label with proper priority
  const getArabicStatusLabel = (order) => {
    // Always prioritize our Arabic mapping over backend status_ar
    // This ensures consistency when admin updates status in English
    
    if (order.status) {
      // First try to get Arabic label from our comprehensive mapping
      const arabicLabel = getOrderStatus(order.status).label;
      
      // Only use backend status_ar if our mapping returns the original status
      // (meaning we don't have a translation for it)
      if (arabicLabel !== order.status && arabicLabel !== 'غير محدد') {
        return arabicLabel;
      }
      
      // Fallback to backend status_ar if available and our mapping didn't find a match
      if (order.status_ar && order.status_ar.trim() !== '') {
        return order.status_ar;
      }
      
      // If we have a status but no Arabic translation, return our mapping result
      return arabicLabel;
    }
    
    // Final fallback if no status at all
    return 'غير محدد';
  };

  // Helper function to get Arabic delivery method label
  const getDeliveryMethodLabel = (method) => {
    const deliveryMethods = {
      'mediator': '  شركة الوسيط ',
      'transport': ' النقليات ',
      'home_delivery': 'توصيل للمنزل',
      'store_pickup': 'استلام من المحل',
      'courier': 'خدمة التوصيل',
      'express': 'توصيل سريع',
      'standard': 'توصيل عادي',
      'free_delivery': 'توصيل مجاني',
      'paid_delivery': 'توصيل مدفوع',
      'same_day': 'توصيل نفس اليوم',
      'next_day': 'توصيل اليوم التالي',
      'cash_on_delivery': 'الدفع عند الاستلام',
      'prepaid': 'مدفوع مسبقاً'
    };

    // Try exact match first, then lowercase, then return original or fallback
    return deliveryMethods[method] || 
           deliveryMethods[method?.toLowerCase()] || 
           method || 'غير محدد';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!phone.trim() || !orderCode.trim()) {
      setError('يرجى إدخال رقم الهاتف ورمز الطلب');
      return;
    }

    setResult(null);
    setError('');
    setLoading(true);
    
    try {
      const res = await axios.post(
        "https://al-sadik.com/api/order_status.php",
        {
          phone,
          order_code: orderCode,
        },
        {
          withCredentials: true,
        }
      );
      
      if (res.data && res.data.success) {
        setResult(res.data);
        // Scroll to top to show the order details
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        setError(res.data.error || "حدث خطأ ما. حاول مرة أخرى.");
      }
    } catch (err) {
      setError(
        err.response?.data?.error ||
        "لم يتم العثور على الطلب أو بيانات غير صحيحة أو هناك مشكلة في الاتصال."
      );
    }
    
    setLoading(false);
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    // Clear previous results when switching tabs
    setResult(null);
    setError('');
    setSelectedOrder(null);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'غير محدد';
    return dateString;
  };

  const formatPrice = (price) => {
    return Number(price || 0).toLocaleString() + ' د.ع';
  };

  const calculateFinalTotal = (result) => {
    // The result.total already includes items + delivery cost
    // So we only need to subtract the discount
    const totalWithDelivery = Number(result.total || 0);
    const discountAmount = Number(result.discount_amount ?? 0);
    
    return totalWithDelivery - discountAmount;
  };

  const calculateItemsOnlyTotal = (result) => {
    // Calculate items total without delivery cost
    const totalWithDelivery = Number(result.total || 0);
    const deliveryCost = Number(result.delivery ?? result.delivery_cost ?? 0);
    
    return totalWithDelivery - deliveryCost;
  };

  return (
    <>
      {globalStyles}
      <Box sx={{ minHeight: '100vh', backgroundColor: '#f8f9fa', pt: { xs: 10, md: 12 } }}>
      
      {/* Order Details - Top Position */}
      {result && (
        <Box sx={{ 
          backgroundColor: '#f8f9fa',
          borderBottom: '2px solid #e9ecef',
          mb: 4,
          mt: 4,
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          position: 'relative',
          zIndex: 100
        }}>
          {/* Close Button - Modern Floating */}
          <Box sx={{ 
            position: 'absolute',
            top: 16,
            right: 16,
            zIndex: 101
          }}>
            <Button
              variant="contained"
              onClick={closeOrderDetails}
              sx={{
                minWidth: 'auto',
                width: 44,
                height: 44,
                borderRadius: '12px',
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                color: '#6b7280',
                fontSize: '1.3rem',
                fontWeight: '600',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08), 0 1px 3px rgba(0, 0, 0, 0.1)',
                border: '1px solid rgba(229, 231, 235, 0.8)',
                backdropFilter: 'blur(12px)',
                '&:hover': {
                  backgroundColor: 'rgba(248, 250, 252, 0.98)',
                  color: '#374151',
                  transform: 'translateY(-2px) scale(1.02)',
                  boxShadow: '0 8px 30px rgba(0, 0, 0, 0.12), 0 2px 6px rgba(0, 0, 0, 0.15)',
                },
                '&:active': {
                  transform: 'translateY(0) scale(0.98)',
                  boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
                },
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  inset: 0,
                  borderRadius: '12px',
                  background: 'linear-gradient(135deg, rgba(255,255,255,0.3) 0%, rgba(255,255,255,0.1) 100%)',
                  pointerEvents: 'none'
                }
              }}
            >
              ✕
            </Button>
          </Box>

          {/* Enhanced Order Status Header */}
          <Box sx={{ 
            width: '100vw',
            marginLeft: 'calc(-50vw + 50%)',
            py: { xs: 3, md: 5 },
            background: `linear-gradient(135deg, 
              ${getOrderStatus(result.status).color === 'success' ? '#28a745' : 
                getOrderStatus(result.status).color === 'error' ? '#dc3545' :
                getOrderStatus(result.status).color === 'warning' ? '#ffc107' :
                getOrderStatus(result.status).color === 'info' ? '#17a2b8' :
                '#3b5dac'} 0%, 
              ${getOrderStatus(result.status).color === 'success' ? '#34ce57' : 
                getOrderStatus(result.status).color === 'error' ? '#e55a5a' :
                getOrderStatus(result.status).color === 'warning' ? '#ffcd39' :
                getOrderStatus(result.status).color === 'info' ? '#20c9e0' :
                '#4c6bc5'} 50%, 
              ${getOrderStatus(result.status).color === 'success' ? '#40d96b' : 
                getOrderStatus(result.status).color === 'error' ? '#ed6b6b' :
                getOrderStatus(result.status).color === 'warning' ? '#ffd454' :
                getOrderStatus(result.status).color === 'info' ? '#2dd4e8' :
                '#5a7bd8'} 100%)`,
            color: 'white',
            textAlign: 'center',
            mb: 3,
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: `
                radial-gradient(circle at 20% 30%, rgba(255,255,255,0.15) 0%, transparent 40%),
                radial-gradient(circle at 80% 20%, rgba(255,255,255,0.1) 0%, transparent 50%),
                radial-gradient(circle at 40% 70%, rgba(255,255,255,0.08) 0%, transparent 60%)
              `,
              pointerEvents: 'none'
            },
            '&::after': {
              content: '""',
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              height: '6px',
              background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.3) 50%, transparent 100%)'
            }
          }}>
            <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
              {/* Status Progress Steps - Enhanced iOS Style */}
              <Box sx={{ 
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                mb: 3,
                flexWrap: { xs: 'nowrap', md: 'wrap' },
                overflowX: { xs: 'auto', md: 'visible' },
                gap: { xs: 0.5, sm: 1, md: 2 },
                px: { xs: 2, md: 0 },
                '&::-webkit-scrollbar': {
                  height: 8,
                  display: { xs: 'block', md: 'none' }
                },
                '&::-webkit-scrollbar-track': {
                  backgroundColor: 'rgba(255,255,255,0.1)',
                  borderRadius: 4
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: 'rgba(255,255,255,0.4)',
                  borderRadius: 4,
                  '&:hover': {
                    backgroundColor: 'rgba(255,255,255,0.5)'
                  }
                }
              }}>
                {['pending', 'confirmed', 'processing', 'shipped', 'delivered'].map((step, index) => {
                  const stepStatus = getOrderStatus(step);
                  const isActive = step === result.status?.toLowerCase();
                  const isPassed = index < ['pending', 'confirmed', 'processing', 'shipped', 'delivered'].indexOf(result.status?.toLowerCase());
                  
                  return (
                    <Box key={step} sx={{ 
                      display: 'flex', 
                      flexDirection: 'column', 
                      alignItems: 'center',
                      minWidth: { xs: '60px', sm: '70px', md: 'auto' },
                      opacity: isActive ? 1 : isPassed ? 0.8 : 0.4,
                      transform: isActive ? { xs: 'scale(1.05)', md: 'scale(1.1)' } : 'scale(1)',
                      transition: 'all 0.3s ease',
                      animation: isActive ? 'pulse 2s infinite' : 'none'
                    }}>
                      <Box sx={{
                        width: { xs: 36, sm: 42, md: 50 },
                        height: { xs: 36, sm: 42, md: 50 },
                        borderRadius: '50%',
                        backgroundColor: isActive ? 'rgba(255,255,255,0.3)' : isPassed ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.1)',
                        border: `${isActive ? 3 : 2}px solid ${isActive ? 'white' : isPassed ? 'rgba(255,255,255,0.6)' : 'rgba(255,255,255,0.3)'}`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 0.5,
                        backdropFilter: 'blur(10px)',
                        boxShadow: isActive ? '0 6px 20px rgba(0,0,0,0.3)' : '0 3px 12px rgba(0,0,0,0.2)'
                      }}>
                        {React.cloneElement(stepStatus.icon, { 
                          sx: { 
                            fontSize: { xs: '1rem', sm: '1.1rem', md: '1.5rem' }, 
                            color: 'white',
                            filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))'
                          } 
                        })}
                      </Box>
                      <Typography variant="caption" sx={{ 
                        fontWeight: isActive ? 'bold' : 'medium',
                        fontSize: { xs: '0.65rem', sm: '0.7rem', md: '0.8rem' },
                        textAlign: 'center',
                        textShadow: '0 1px 2px rgba(0,0,0,0.3)',
                        maxWidth: { xs: '55px', sm: '65px', md: '80px' },
                        lineHeight: 1.2
                      }}>
                        {stepStatus.label}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>

              {/* Main Status Display */}
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                mb: 2,
                animation: 'fadeInUp 0.6s ease-out',
                px: { xs: 2, md: 0 }
              }}>
                <Box sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: 'center',
                  gap: { xs: 1.5, sm: 2 },
                  p: { xs: 2.5, sm: 3 },
                  backgroundColor: 'rgba(255,255,255,0.15)',
                  borderRadius: 3,
                  backdropFilter: 'blur(15px)',
                  border: '2px solid rgba(255,255,255,0.3)',
                  boxShadow: '0 8px 30px rgba(0,0,0,0.3)',
                  animation: 'zoomIn 0.8s ease-out 0.3s both',
                  width: { xs: '100%', sm: 'auto' }
                }}>
                  {React.cloneElement(getOrderStatus(result.status).icon, { 
                    sx: { 
                      fontSize: { xs: '1.8rem', sm: '2rem', md: '2.5rem' }, 
                      color: 'white',
                      filter: 'drop-shadow(0 3px 6px rgba(0,0,0,0.4))',
                      animation: 'pulse 2s infinite'
                    } 
                  })}
                  <Box sx={{ textAlign: { xs: 'center', sm: 'right' } }}>
                    <Typography variant="h6" sx={{ 
                      fontWeight: 'medium', 
                      opacity: 0.9,
                      fontSize: { xs: '0.85rem', sm: '0.9rem', md: '1rem' },
                      textShadow: '0 2px 4px rgba(0,0,0,0.3)'
                    }}>
                      حالة الطلب الحالية
                    </Typography>
                    <Typography variant="h3" sx={{ 
                      fontWeight: 'bold',
                      fontSize: { xs: '1.3rem', sm: '1.5rem', md: '2rem' },
                      textShadow: '0 3px 6px rgba(0,0,0,0.4)',
                      mt: 0.5
                    }}>
                      {getOrderStatus(result.status).label}
                    </Typography>
                  </Box>
                </Box>
              </Box>
              
              {/* Order Info Cards - Removed as requested */}
            </Container>
          </Box>

          {/* Content Area */}
          <Container maxWidth="lg" sx={{ px: { xs: 2, md: 3 } }}>
            {/* Customer Info */}
            <Card sx={{ 
              mb: 4,
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
              borderRadius: { xs: 2, md: 3 },
              border: '1px solid #e9ecef'
            }}>
              <CardContent sx={{ p: { xs: 2.5, sm: 3, md: 4 } }}>
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, mb: { xs: 2.5, md: 4 }, gap: { xs: 2, sm: 0 } }}>
                  <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#3b5dac', display: 'flex', alignItems: 'center', fontSize: { xs: '1.1rem', sm: '1.25rem', md: '1.5rem' } }}>
                    <PersonIcon sx={{ mr: { xs: 1, md: 2 }, fontSize: { xs: '1.3rem', md: '1.5rem' } }} />
                    معلومات العميل
                  </Typography>
                  <Button
                    variant="outlined"
                    onClick={() => setShowFullDetails(!showFullDetails)}
                    endIcon={showFullDetails ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    size={isMobile ? "small" : "medium"}
                    sx={{ 
                      color: '#3b5dac',
                      borderColor: '#3b5dac',
                      backgroundColor: 'transparent',
                      fontWeight: 'medium',
                      borderRadius: 2,
                      px: { xs: 2, sm: 3 },
                      py: { xs: 0.5, sm: 1 },
                      fontSize: { xs: '0.85rem', sm: '0.9rem' },
                      alignSelf: { xs: 'stretch', sm: 'auto' },
                      '&:hover': { 
                        backgroundColor: 'rgba(59, 93, 172, 0.1)',
                        borderColor: '#2a406e',
                        color: '#2a406e',
                        transform: 'translateY(-1px)',
                        boxShadow: '0 4px 12px rgba(59, 93, 172, 0.2)'
                      },
                      transition: 'all 0.2s ease'
                    }}
                  >
                    {showFullDetails ? 'إخفاء التفاصيل' : 'عرض التفاصيل'}
                  </Button>
                </Box>

                {/* Always visible: Name and City - iOS Style */}
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: { xs: 1.5, sm: 3, md: 4 }, mb: showFullDetails ? { xs: 2, md: 4 } : 0 }}>
                  <Box sx={{ 
                    p: { xs: 2, sm: 2.5, md: 3 }, 
                    backgroundColor: { xs: '#ffffff', sm: '#f8f9fa' },
                    borderRadius: { xs: 2.5, sm: 2 },
                    border: { xs: '1px solid #e9ecef', sm: 'none' },
                    boxShadow: { xs: '0 1px 3px rgba(0,0,0,0.08)', sm: 'none' }
                  }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' }, fontWeight: { xs: 600, sm: 'normal' }, letterSpacing: { xs: '0.3px', sm: 'normal' } }}>
                      الاسم
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: { xs: '0.95rem', sm: '1.1rem', md: '1.25rem' }, color: { xs: '#1c1c1e', sm: 'inherit' } }}>
                      {result.customer?.name}
                    </Typography>
                  </Box>

                  <Box sx={{ 
                    p: { xs: 2, sm: 2.5, md: 3 }, 
                    backgroundColor: { xs: '#ffffff', sm: '#f8f9fa' },
                    borderRadius: { xs: 2.5, sm: 2 },
                    border: { xs: '1px solid #e9ecef', sm: 'none' },
                    boxShadow: { xs: '0 1px 3px rgba(0,0,0,0.08)', sm: 'none' }
                  }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' }, fontWeight: { xs: 600, sm: 'normal' }, letterSpacing: { xs: '0.3px', sm: 'normal' } }}>
                      المدينة
                    </Typography>
                    <Typography variant="h6" sx={{ fontSize: { xs: '0.95rem', sm: '1.1rem', md: '1.25rem' }, color: { xs: '#1c1c1e', sm: 'inherit' } }}>
                      {result.customer?.city}
                    </Typography>
                  </Box>
                </Box>

                {/* Expandable details */}
                {showFullDetails && (
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: { xs: 2, sm: 3, md: 4 } }}>
                    <Box sx={{ p: { xs: 2, sm: 2.5, md: 3 }, backgroundColor: '#f8f9fa', borderRadius: 2 }}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                        الهاتف
                      </Typography>
                      <Typography variant="h6" sx={{ fontSize: { xs: '0.95rem', sm: '1.1rem', md: '1.25rem' }, direction: 'ltr', textAlign: 'right' }}>
                        {result.customer?.phone}
                      </Typography>
                    </Box>

                    <Box sx={{ p: { xs: 2, sm: 2.5, md: 3 }, backgroundColor: '#f8f9fa', borderRadius: 2 }}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                        طريقة التوصيل
                      </Typography>
                      <Typography variant="h6" sx={{ fontSize: { xs: '0.95rem', sm: '1.1rem', md: '1.25rem' } }}>
                        {getDeliveryMethodLabel(result.delivery_method)}
                      </Typography>
                    </Box>

                    <Box sx={{ gridColumn: { xs: '1', sm: '1 / -1' }, p: { xs: 2, sm: 2.5, md: 3 }, backgroundColor: '#f8f9fa', borderRadius: 2 }}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                        العنوان
                      </Typography>
                      <Typography variant="h6" sx={{ fontSize: { xs: '0.95rem', sm: '1.1rem', md: '1.25rem' } }}>
                        {result.customer?.address}
                      </Typography>
                    </Box>

                    {result.customer?.note && (
                      <Box sx={{ gridColumn: { xs: '1', sm: '1 / -1' }, p: { xs: 2, sm: 2.5, md: 3 }, backgroundColor: '#fff3cd', borderRadius: 2, border: '1px solid #ffeaa7' }}>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                          ملاحظات
                        </Typography>
                        <Typography variant="h6" sx={{ fontSize: { xs: '0.95rem', sm: '1.1rem', md: '1.25rem' } }}>
                          {result.customer.note}
                        </Typography>
                      </Box>
                    )}

                    <Box sx={{ gridColumn: { xs: '1', sm: '1 / -1' }, p: { xs: 2, sm: 2.5, md: 3 }, backgroundColor: '#f8f9fa', borderRadius: 2 }}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                        تاريخ الطلب
                      </Typography>
                      <Typography variant="h6" sx={{ fontSize: { xs: '0.95rem', sm: '1.1rem', md: '1.25rem' } }}>
                        {formatDate(result.created_at)}
                      </Typography>
                    </Box>
                  </Box>
                )}
              </CardContent>
            </Card>

            {/* Order Items */}
            <Card sx={{ 
              mb: 4,
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
              borderRadius: { xs: 2, md: 3 },
              border: '1px solid #e9ecef'
            }}>
              <CardContent sx={{ p: { xs: 2.5, sm: 3, md: 4 } }}>
                <Typography variant="h5" sx={{ fontWeight: 'bold', mb: { xs: 2.5, md: 4 }, color: '#3b5dac', display: 'flex', alignItems: 'center', fontSize: { xs: '1.1rem', sm: '1.25rem', md: '1.5rem' } }}>
                  <ShoppingCartIcon sx={{ mr: { xs: 1, md: 2 }, fontSize: { xs: '1.3rem', md: '1.5rem' } }} />
                  تفاصيل الطلب
                </Typography>
            
            {/* Desktop Table */}
            <Box sx={{ display: { xs: 'none', md: 'block' }, mb: 3 }}>
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                      <TableCell sx={{ fontWeight: 'bold', width: 80 }}>الصورة</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>اسم المنتج</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>الماركة</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>SKU</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>الكمية</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>السعر</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>المجموع</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(result.items || []).map((item) => (
                      <TableRow key={item.id}>
                        <TableCell>
                          <Box
                            component="img"
                            src={
                              item.image && typeof item.image === 'string' && item.image.trim() ?
                                (item.image.startsWith('http') ?
                                  item.image :
                                  `https://al-sadik.com/api/uploads/products/${item.image.replace('uploads/', '')}`
                                ) :
                                'https://via.placeholder.com/60x60?text=No+Image'
                            }
                            alt={item.product_name}
                            sx={{
                              width: 60,
                              height: 60,
                              objectFit: 'cover',
                              borderRadius: 1,
                              border: '1px solid #e9ecef'
                            }}
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = 'https://via.placeholder.com/60x60?text=No+Image';
                            }}
                          />
                        </TableCell>
                        <TableCell>{item.product_name}</TableCell>
                        <TableCell>{item.brand_name}</TableCell>
                        <TableCell>{item.product_sku}</TableCell>
                        <TableCell>{item.quantity}</TableCell>
                        <TableCell>{formatPrice(item.price)}</TableCell>
                        <TableCell>{formatPrice(item.total_price)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                  <TableFooter>
                    <TableRow>
                      <TableCell colSpan={5} sx={{ textAlign: 'right', fontWeight: 'bold' }}>
                        مجموع المنتجات
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>
                        {formatPrice(calculateItemsOnlyTotal(result))}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={5} sx={{ textAlign: 'right', fontWeight: 'bold' }}>
                        تكلفة التوصيل
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>
                        {formatPrice(result.delivery ?? result.delivery_cost ?? 0)}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={5} sx={{ textAlign: 'right', fontWeight: 'bold' }}>
                        المجموع الفرعي
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>
                        {formatPrice(result.total)}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={5} sx={{ textAlign: 'right', fontWeight: 'bold' }}>
                        قيمة الخصم
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold', color: '#dc3545' }}>
                        -{formatPrice(result.discount_amount ?? 0)}
                      </TableCell>
                    </TableRow>
                    <TableRow sx={{ backgroundColor: '#e8f5e8' }}>
                      <TableCell colSpan={5} sx={{ textAlign: 'right', fontWeight: 'bold', fontSize: '1.1rem' }}>
                        المجموع النهائي
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold', fontSize: '1.1rem', color: '#3b5dac' }}>
                        {formatPrice(calculateFinalTotal(result))}
                      </TableCell>
                    </TableRow>
                  </TableFooter>
                </Table>
              </TableContainer>
            </Box>

            {/* Mobile Products View - iOS Style */}
            <Box sx={{ display: { xs: 'block', md: 'none' } }}>
              {(result.items || []).map((item, index) => (
                <Card key={item.id} variant="outlined" sx={{ 
                  mb: 2,
                  borderRadius: 3,
                  boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                  border: '1px solid rgba(0,0,0,0.06)',
                  backgroundColor: '#ffffff',
                  overflow: 'hidden',
                  '&:hover': {
                    boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
                    transform: 'translateY(-1px)',
                    transition: 'all 0.2s ease'
                  },
                  '&:active': {
                    transform: 'scale(0.98)',
                    transition: 'all 0.1s ease'
                  }
                }}>
                  <CardContent sx={{ p: 2.5 }}>
                    <Box sx={{ display: 'flex', gap: 2, mb: 2.5 }}>
                      <Box
                        component="img"
                        src={
                          item.image && typeof item.image === 'string' && item.image.trim() ?
                            (item.image.startsWith('http') ?
                              item.image :
                              `https://al-sadik.com/api/uploads/products/${item.image.replace('uploads/', '')}`
                            ) :
                            'https://via.placeholder.com/80x80?text=No+Image'
                        }
                        alt={item.product_name}
                        sx={{
                          width: 85,
                          height: 85,
                          objectFit: 'cover',
                          borderRadius: 2.5,
                          border: '1px solid rgba(0,0,0,0.06)',
                          flexShrink: 0,
                          backgroundColor: '#f8f9fa'
                        }}
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = 'https://via.placeholder.com/80x80?text=No+Image';
                        }}
                      />
                      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                          <Box sx={{ 
                            minWidth: 28,
                            height: 28,
                            borderRadius: '50%',
                            backgroundColor: '#3b5dac',
                            color: 'white',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontWeight: 'bold',
                            fontSize: '0.75rem',
                            mr: 1,
                            flexShrink: 0
                          }}>
                            {index + 1}
                          </Box>
                          <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: '1.15rem !important', lineHeight: 1.3, flex: 1 }}>
                            {item.product_name}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                    
                    <Box sx={{ 
                      display: 'grid', 
                      gridTemplateColumns: 'repeat(2, 1fr)', 
                      gap: 2,
                      mt: 2.5,
                      pt: 2.5,
                      borderTop: '1px solid rgba(0,0,0,0.06)'
                    }}>
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', display: 'block', mb: 0.5 }}>
                          الماركة
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 'medium', fontSize: '0.875rem' }}>
                          {item.brand_name || '-'}
                        </Typography>
                      </Box>
                      
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', display: 'block', mb: 0.5 }}>
                          SKU
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 'medium', fontSize: '0.875rem', direction: 'ltr', textAlign: 'right' }}>
                          {item.product_sku || '-'}
                        </Typography>
                      </Box>
                      
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', display: 'block', mb: 0.5 }}>
                          الكمية
                        </Typography>
                        <Chip 
                          label={item.quantity} 
                          size="small" 
                          sx={{ 
                            backgroundColor: '#e8f5e8',
                            color: '#2e7d32',
                            fontWeight: 'bold',
                            height: 28,
                            fontSize: '1rem !important',
                            '& .MuiChip-label': {
                              fontSize: '1rem',
                              padding: '0 10px'
                            }
                          }} 
                        />
                      </Box>
                      
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', display: 'block', mb: 0.5 }}>
                          السعر
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 'medium', fontSize: '0.875rem' }}>
                          {formatPrice(item.price)}
                        </Typography>
                      </Box>
                    </Box>
                    
                    <Box sx={{ 
                      mt: 2, 
                      pt: 2,
                      borderTop: '2px solid #e9ecef',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      backgroundColor: '#f8f9fa',
                      borderRadius: 1.5,
                      p: 1.5,
                      mx: -1
                    }}>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'text.secondary', fontSize: '0.875rem' }}>
                        المجموع
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#3b5dac', fontSize: '1.1rem' }}>
                        {formatPrice(item.total_price)}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              ))}
              
              {/* Mobile Summary Card - iOS Style */}
              <Card variant="outlined" sx={{ 
                backgroundColor: '#f8f9fa',
                borderRadius: 3,
                boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                border: '1px solid rgba(0,0,0,0.06)',
                overflow: 'hidden'
              }}>
                <CardContent sx={{ p: 2.5 }}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, color: '#3b5dac', fontSize: '1rem' }}>
                    ملخص الطلب
                  </Typography>
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5, pb: 1.5 }}>
                    <Typography variant="body2" sx={{ fontSize: '0.9rem' }}>مجموع المنتجات</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 'medium', fontSize: '0.9rem' }}>
                      {formatPrice(calculateItemsOnlyTotal(result))}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5, pb: 1.5 }}>
                    <Typography variant="body2" sx={{ fontSize: '0.9rem' }}>تكلفة التوصيل</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 'medium', fontSize: '0.9rem' }}>
                      {formatPrice(result.delivery ?? result.delivery_cost ?? 0)}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5, pb: 1.5 }}>
                    <Typography variant="body2" sx={{ fontSize: '0.9rem' }}>المجموع الفرعي</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 'medium', fontSize: '0.9rem' }}>
                      {formatPrice(result.total)}
                    </Typography>
                  </Box>
                  
                  {(result.discount_amount ?? 0) > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5, pb: 1.5 }}>
                      <Typography variant="body2" sx={{ fontSize: '0.9rem' }}>قيمة الخصم</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 'medium', fontSize: '0.9rem', color: '#dc3545' }}>
                        -{formatPrice(result.discount_amount ?? 0)}
                      </Typography>
                    </Box>
                  )}
                  
                  <Divider sx={{ my: 1.5 }} />
                  
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    p: 2, 
                    backgroundColor: '#e8f5e8', 
                    borderRadius: 1.5,
                    border: '2px solid #c3e6c3'
                  }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: '1.05rem' }}>
                      المجموع النهائي
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#3b5dac', fontSize: '1.2rem' }}>
                      {formatPrice(calculateFinalTotal(result))}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Box>
              </CardContent>
            </Card>

            {/* PDF Download Button */}
            {result.pdf_url && (
              <Box sx={{ mt: { xs: 3, md: 4 }, textAlign: 'center', px: { xs: 2, md: 0 } }}>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<DownloadIcon />}
                  href={result.pdf_url.startsWith("http") ? result.pdf_url : `https://al-sadik.com${result.pdf_url}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  fullWidth={isMobile}
                  sx={{
                    backgroundColor: '#28a745',
                    color: 'white',
                    fontWeight: 'bold',
                    px: { xs: 3, md: 4 },
                    py: { xs: 1.5, md: 1.5 },
                    fontSize: { xs: '1rem', md: '1.1rem' },
                    borderRadius: 2,
                    boxShadow: '0 4px 12px rgba(40, 167, 69, 0.3)',
                    '&:hover': {
                      backgroundColor: '#218838',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 6px 16px rgba(40, 167, 69, 0.4)',
                    },
                    '&:active': {
                      backgroundColor: '#1e7e34',
                      transform: 'translateY(0)',
                    },
                    transition: 'all 0.2s ease'
                  }}
                >
                  {isMobile ? 'تحميل الإيصال' : 'تحميل الإيصال PDF'}
                </Button>
              </Box>
            )}
          </Container>
        </Box>
      )}

      <Container maxWidth="md" sx={{ py: 4 }}>
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#3b5dac', mb: 2 }}>
            طلباتي
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {user ? 'عرض سجل طلباتك أو البحث عن طلب محدد' : 'أدخل رقم الهاتف ورمز الطلب للاستعلام عن حالة طلبك'}
          </Typography>
        </Box>

        {/* Tabs - Only show if user is logged in */}
        {user && (
          <Card sx={{ mb: 4, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', borderRadius: 3 }}>
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              centered
              sx={{
                '& .MuiTab-root': {
                  color: '#666',
                  fontWeight: 'medium',
                  fontSize: '1rem',
                  '&.Mui-selected': {
                    color: '#3b5dac',
                    fontWeight: 'bold'
                  }
                },
                '& .MuiTabs-indicator': {
                  backgroundColor: '#3b5dac',
                  height: 3
                }
              }}
            >
              <Tab
                icon={<HistoryIcon />}
                label="سجل الطلبات"
                iconPosition="start"
                sx={{ px: 4 }}
              />
              <Tab
                icon={<SearchIcon />}
                label="البحث عن طلب"
                iconPosition="start"
                sx={{ px: 4 }}
              />
            </Tabs>
          </Card>
        )}

        {/* Order History Tab */}
        {user && tabValue === 0 && (
          <>
            {historyError && (
              <Alert severity="error" sx={{ mb: 4 }}>
                {historyError}
              </Alert>
            )}

            {historyLoading && (
              <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                <CircularProgress size={60} />
              </Box>
            )}

            {!historyLoading && orders.length === 0 && !historyError && (
              <Card sx={{ textAlign: 'center', p: 6 }}>
                <HistoryIcon sx={{ fontSize: 64, color: '#ccc', mb: 2 }} />
                <Typography variant="h6" color="text.secondary">
                  لا توجد طلبات لهذا الحساب
                </Typography>
              </Card>
            )}

            {!historyLoading && orders.length > 0 && (
              <Card sx={{ mb: 4, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', borderRadius: 3 }}>
                <CardContent sx={{ p: 4 }}>
                  <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 4, color: '#3b5dac', display: 'flex', alignItems: 'center' }}>
                    <HistoryIcon sx={{ mr: 2 }} />
                    سجل الطلبات
                  </Typography>

                  {/* Desktop Table */}
                  <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                    <TableContainer component={Paper} variant="outlined">
                      <Table>
                        <TableHead>
                          <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                            <TableCell sx={{ fontWeight: 'bold' }}>رقم الطلب</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>تاريخ الطلب</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>الحالة</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>عرض</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>طباعة</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {orders.map((order) => (
                            <TableRow key={order.id}>
                              <TableCell>{order.order_code}</TableCell>
                              <TableCell>{formatDate(order.created_at)}</TableCell>
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Box sx={{
                                    width: 8,
                                    height: 8,
                                    borderRadius: '50%',
                                    backgroundColor: 
                                      getOrderStatus(order.status).color === 'success' ? '#28a745' :
                                      getOrderStatus(order.status).color === 'error' ? '#dc3545' :
                                      getOrderStatus(order.status).color === 'warning' ? '#ffc107' :
                                      getOrderStatus(order.status).color === 'info' ? '#17a2b8' :
                                      '#3b5dac',
                                    animation: getOrderStatus(order.status).color === 'warning' ? 'pulse 2s infinite' : 'none'
                                  }} />
                                  <Chip
                                    label={getArabicStatusLabel(order)}
                                    color={getOrderStatus(order.status).color}
                                    size="small"
                                    icon={getOrderStatus(order.status).icon}
                                    sx={{
                                      fontWeight: 'medium',
                                      '& .MuiChip-icon': {
                                        fontSize: '1rem'
                                      },
                                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                      border: '1px solid rgba(0,0,0,0.1)'
                                    }}
                                  />
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Button
                                  variant={selectedOrder === order.order_code ? "contained" : "outlined"}
                                  size="small"
                                  onClick={() => showOrderDetails(order.order_code)}
                                  disabled={detailsLoading}
                                  sx={{ 
                                    color: selectedOrder === order.order_code ? 'white' : '#3b5dac',
                                    backgroundColor: selectedOrder === order.order_code ? '#3b5dac' : 'transparent',
                                    borderColor: '#3b5dac',
                                    minWidth: '80px',
                                    fontWeight: 'medium',
                                    borderRadius: 2,
                                    '&:hover': {
                                      backgroundColor: selectedOrder === order.order_code ? '#2a406e' : 'rgba(59, 93, 172, 0.1)',
                                      borderColor: '#2a406e',
                                      transform: 'translateY(-1px)',
                                      boxShadow: '0 4px 12px rgba(59, 93, 172, 0.3)'
                                    },
                                    transition: 'all 0.2s ease'
                                  }}
                                >
                                  {detailsLoading && selectedOrder === order.order_code ? (
                                    <CircularProgress size={16} color="inherit" />
                                  ) : (
                                    'عرض'
                                  )}
                                </Button>
                              </TableCell>
                              <TableCell>
                                <Button
                                  variant="outlined"
                                  size="small"
                                  startIcon={<PrintIcon />}
                                  href={`${API_BASE_URL}/api/order_pdf.php?order_code=${order.order_code}&user_id=${user.id}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  sx={{ 
                                    color: 'white',
                                    backgroundColor: '#28a745',
                                    borderColor: '#28a745',
                                    minWidth: '100px',
                                    fontWeight: 'medium',
                                    borderRadius: 2,
                                    '&:hover': {
                                      backgroundColor: '#218838',
                                      borderColor: '#218838',
                                      color: 'white',
                                      transform: 'translateY(-1px)',
                                      boxShadow: '0 4px 12px rgba(40, 167, 69, 0.3)'
                                    },
                                    '&:active': {
                                      backgroundColor: '#1e7e34',
                                      borderColor: '#1e7e34'
                                    },
                                    transition: 'all 0.2s ease'
                                  }}
                                >
                                  طباعة
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>

                  {/* Mobile Cards */}
                  <Box sx={{ display: { xs: 'block', md: 'none' } }}>
                    {orders.map((order) => (
                      <Card key={order.id} variant="outlined" sx={{ mb: 2 }}>
                        <CardContent>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                              #{order.order_code}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Box sx={{
                                width: 10,
                                height: 10,
                                borderRadius: '50%',
                                backgroundColor: 
                                  getOrderStatus(order.status).color === 'success' ? '#28a745' :
                                  getOrderStatus(order.status).color === 'error' ? '#dc3545' :
                                  getOrderStatus(order.status).color === 'warning' ? '#ffc107' :
                                  getOrderStatus(order.status).color === 'info' ? '#17a2b8' :
                                  '#3b5dac',
                                animation: getOrderStatus(order.status).color === 'warning' ? 'pulse 2s infinite' : 'none',
                                boxShadow: '0 2px 6px rgba(0,0,0,0.2)'
                              }} />
                              <Chip
                                label={getArabicStatusLabel(order)}
                                color={getOrderStatus(order.status).color}
                                size="small"
                                icon={getOrderStatus(order.status).icon}
                                sx={{
                                  fontWeight: 'medium',
                                  fontSize: '0.8rem',
                                  '& .MuiChip-icon': {
                                    fontSize: '1rem'
                                  },
                                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                                  border: '1px solid rgba(0,0,0,0.1)'
                                }}
                              />
                            </Box>
                          </Box>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            تاريخ الطلب: {formatDate(order.created_at)}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                            <Button
                              variant={selectedOrder === order.order_code ? "contained" : "outlined"}
                              size="medium"
                              onClick={() => showOrderDetails(order.order_code)}
                              disabled={detailsLoading}
                              sx={{ 
                                color: selectedOrder === order.order_code ? 'white' : '#3b5dac',
                                backgroundColor: selectedOrder === order.order_code ? '#3b5dac' : 'transparent',
                                borderColor: '#3b5dac',
                                flex: 1,
                                minWidth: '120px',
                                fontWeight: 'medium',
                                borderRadius: 2,
                                py: 1,
                                '&:hover': {
                                  backgroundColor: selectedOrder === order.order_code ? '#2a406e' : 'rgba(59, 93, 172, 0.1)',
                                  borderColor: '#2a406e',
                                  transform: 'translateY(-2px)',
                                  boxShadow: '0 6px 16px rgba(59, 93, 172, 0.3)'
                                },
                                transition: 'all 0.3s ease'
                              }}
                            >
                              {detailsLoading && selectedOrder === order.order_code ? (
                                <CircularProgress size={18} color="inherit" />
                              ) : (
                                'عرض التفاصيل'
                              )}
                            </Button>
                            <Button
                              variant="contained"
                              size="medium"
                              startIcon={<PrintIcon />}
                              href={`${API_BASE_URL}/api/order_pdf.php?order_code=${order.order_code}&user_id=${user.id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{ 
                                color: 'white',
                                backgroundColor: '#28a745',
                                borderColor: '#28a745',
                                flex: 1,
                                minWidth: '120px',
                                fontWeight: 'medium',
                                borderRadius: 2,
                                py: 1,
                                '&:hover': {
                                  backgroundColor: '#218838',
                                  borderColor: '#218838',
                                  color: 'white',
                                  transform: 'translateY(-2px)',
                                  boxShadow: '0 6px 16px rgba(40, 167, 69, 0.3)'
                                },
                                '&:active': {
                                  backgroundColor: '#1e7e34',
                                  borderColor: '#1e7e34'
                                },
                                transition: 'all 0.3s ease'
                              }}
                            >
                              طباعة
                            </Button>
                          </Box>
                        </CardContent>
                      </Card>
                    ))}
                  </Box>
                </CardContent>
              </Card>
            )}
          </>
        )}

        {/* Search Tab or Default view for non-logged users - iOS Style */}
        {(!user || tabValue === 1) && (
          <>
            {/* Search Form - iOS Style */}
            <Card sx={{ 
              mb: 6, 
              boxShadow: { xs: '0 1px 3px rgba(0,0,0,0.12)', sm: '0 12px 40px rgba(59, 93, 172, 0.15)' },
              borderRadius: { xs: 3, sm: 4 },
              border: { xs: 'none', sm: '1px solid rgba(59, 93, 172, 0.2)' },
              background: { xs: '#ffffff', sm: 'linear-gradient(135deg, #ffffff 0%, #f8f9ff 100%)' },
              overflow: 'hidden',
              position: 'relative',
              transform: 'translateY(0)',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: { xs: 'none', sm: 'translateY(-5px)' },
                boxShadow: { xs: '0 1px 3px rgba(0,0,0,0.12)', sm: '0 16px 48px rgba(59, 93, 172, 0.2)' },
              },
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: { xs: 0, sm: '5px' },
                background: 'linear-gradient(90deg, #3b5dac 0%, #4c6bc5 50%, #5a7bd8 100%)'
              }
            }}>
              <CardContent sx={{ p: { xs: 3, sm: 5 } }}>
                <Typography 
                  variant="h5" 
                  sx={{ 
                    mb: 4, 
                    textAlign: 'center',
                    color: '#3b5dac',
                    fontWeight: 'bold',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 2,
                    animation: 'fadeInUp 0.8s ease-out',
                    textShadow: '0 2px 4px rgba(59, 93, 172, 0.1)',
                    '& .MuiSvgIcon-root': {
                      animation: 'pulse 2s infinite',
                    }
                  }}
                >
                  <SearchIcon sx={{ fontSize: '2.2rem', color: '#4c6bc5' }} />
                   البحث عن الطلب
                </Typography>
                
                <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <TextField
                    fullWidth
                    label="رقم الهاتف"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          backgroundColor: 'rgba(59, 93, 172, 0.1)',
                          borderRadius: '50%',
                          p: 1,
                          mr: 2
                        }}>
                          <PhoneIcon sx={{ color: '#3b5dac', fontSize: '1.2rem' }} />
                        </Box>
                      )
                    }}
                    placeholder="مثال: 07801234567"
                    variant="outlined"
                    required
                    disabled={loading}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 3,
                        backgroundColor: '#ffffff',
                        fontSize: '1.1rem',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: '0 4px 16px rgba(59, 93, 172, 0.15)',
                        },
                        '&:hover fieldset': {
                          borderColor: '#3b5dac',
                          borderWidth: '2px',
                        },
                        '&.Mui-focused': {
                          transform: 'translateY(-2px)',
                          boxShadow: '0 6px 20px rgba(59, 93, 172, 0.2)',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#3b5dac',
                          borderWidth: '2px',
                        },
                      },
                      '& .MuiInputLabel-root': {
                        color: '#666',
                        '&.Mui-focused': {
                          color: '#3b5dac',
                          fontWeight: 'bold'
                        }
                      }
                    }}
                  />
                  
                  <TextField
                    fullWidth
                    label="رقم الطلب (الكود)"
                    value={orderCode}
                    onChange={(e) => setOrderCode(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          backgroundColor: 'rgba(59, 93, 172, 0.1)',
                          borderRadius: '50%',
                          p: 1,
                          mr: 2
                        }}>
                          <ReceiptIcon sx={{ color: '#3b5dac', fontSize: '1.2rem' }} />
                        </Box>
                      )
                    }}
                    placeholder="مثال: 12345678"
                    variant="outlined"
                    required
                    disabled={loading}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 3,
                        backgroundColor: '#ffffff',
                        fontSize: '1.1rem',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: '0 4px 16px rgba(59, 93, 172, 0.15)',
                        },
                        '&:hover fieldset': {
                          borderColor: '#3b5dac',
                          borderWidth: '2px',
                        },
                        '&.Mui-focused': {
                          transform: 'translateY(-2px)',
                          boxShadow: '0 6px 20px rgba(59, 93, 172, 0.2)',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#3b5dac',
                          borderWidth: '2px',
                        },
                      },
                      '& .MuiInputLabel-root': {
                        color: '#666',
                        '&.Mui-focused': {
                          color: '#3b5dac',
                          fontWeight: 'bold'
                        }
                      }
                    }}
                  />

                  <Button
                    fullWidth
                    variant="contained"
                    size="large"
                    type="submit"
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={24} color="inherit" /> : <SearchIcon sx={{ fontSize: '1.5rem' }} />}
                    sx={{
                      background: 'linear-gradient(135deg, #3b5dac 0%, #4c6bc5 50%, #5a7bd8 100%)',
                      py: 2.5,
                      fontSize: '1.3rem',
                      fontWeight: 'bold',
                      borderRadius: 3,
                      textTransform: 'none',
                      boxShadow: '0 8px 25px rgba(59, 93, 172, 0.4)',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      position: 'relative',
                      overflow: 'hidden',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: '-100%',
                        width: '100%',
                        height: '100%',
                        background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
                        transition: 'left 0.5s',
                      },
                      '&:hover': {
                        background: 'linear-gradient(135deg, #2a406e 0%, #3b5dac 50%, #4c6bc5 100%)',
                        transform: 'translateY(-4px) scale(1.02)',
                        boxShadow: '0 12px 35px rgba(59, 93, 172, 0.5)',
                        '&::before': {
                          left: '100%',
                        },
                      },
                      '&:active': {
                        transform: 'translateY(-2px) scale(1.01)',
                      },
                      '&:disabled': {
                        background: 'linear-gradient(135deg, #ccc 0%, #ddd 100%)',
                        transform: 'none',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                      }
                    }}
                  >
                    {loading ? 'جارٍ البحث...' : 'عرض الطلب'}
                  </Button>
                </Box>
              </CardContent>
            </Card>

            {/* Error Message */}
            {error && (
              <Alert 
                severity="error" 
                sx={{ 
                  mb: 6,
                  borderRadius: 3,
                  boxShadow: '0 4px 16px rgba(211, 47, 47, 0.2)',
                  border: '1px solid rgba(211, 47, 47, 0.3)',
                  '& .MuiAlert-icon': {
                    fontSize: '1.5rem'
                  },
                  fontSize: '1.1rem'
                }}
              >
                {error}
              </Alert>
            )}
          </>
        )}
      </Container>

    </Box>
    </>
  );
};

export default MyOrders;
